<template>
  <div v-has-perms="rolePerm">
    <portal-target
      v-show="showList"
      name="layout-main"
      class="layout-main panel-scroll-full"
    >
      <div class="panel-list panel-fixed">
        <div class="panel-header">
          <h1 class="page-header mr-2 d-flex align-items-center">
            {{ titleWithOrganizationName | translate }}
          </h1>
          <ol class="breadcrumb pull-right">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'dashboard'}">
                {{"Dashboard"}}
              </router-link>
            </li>
            <li v-for="(breadcrumb) in breadcrumbs" :key="breadcrumb.label" class="breadcrumb-item">
              <router-link :to="breadcrumb.route">
                {{ breadcrumb.label | translate }}
              </router-link>
            </li>
            <li class="breadcrumb-item">{{ title | translate }}</li>
          </ol>
        </div>
        <component-edit
          v-if="showEditForm && editComponent"
          :Inspections="list.results"
          :id="componentId"
          :organization="$route.params.id"
          :editParams="editParams"
          @on-submit="refreshListAfterSubmit"
          @close-form='closeEditForm'
        />
        <organization-inspection-attachments-list
          v-if="showAttachments"
          :inspection="inspection.id"
          :inspection-num="inspection.num"
          @hide="showAttachments = false"
        />

        <advanced-search
          v-show="showAdvancedSearch"
          v-if="toggleAdvancedSearch"
          :fields="searchFields"
          :reset="reset"
          @search="addFiltersToInstance"
          @reset="removeFiltersToInstance"
          @active="activeFilters = $event"
        />
        <panel :title="title" :no-button="true">
          <thux-list-component
            :fields="getFields"
            :pagination="getPaginationFields"
            @select-per-page="changePerPage"
            @change-page="changePage"
            :action-select="getActionSelectFields"
            @select-action="setAction"
            @select-or-deselect-all-queryset="selectOrDeselectAllQueryset"
            @do-action="doAction($event, fieldName)"
            :advanced-search="getAdvancedSearchFields"
            @show-advanced-search="openAdvancedSearch"
            @open-edit-form="openEditForm()"
            @open-detail-form="openDetailForm()"
          >
            <template slot="body">
              <component-table
                :list="list.results"
                :goBack="goBack"
                :params="tableParams"
                @open-edit-form="openEditForm"
                @open-detail-form="openDetailForm"
                @show-attachments="showOrganizationInspectionAttachments"
                @refresh-list="refreshListAfterSubmit"
              />
            </template>
          </thux-list-component>
        </panel>
      </div>
    </portal-target>
    <router-view />
    <portal-target
      v-if="showCommonFilters"
      v-show="showList"
      name="layout-sidebar"
      class="layout-side panel-scroll-full"
    >
      <button
        @click.prevent="closeRightSidebar"
        class="btn btn-primary text-uppercase w-100 mb-4"
        :aria-label="$t('Close')"
      >
        <i class="fas fa-times" />
        {{ $t('Hide sidebar') }}
      </button>
      <common-filters />
    </portal-target>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
// controllare import router
import { ROUTES as ORGANIZATION_ROUTES } from '../../router'
import { TYPES as ORGANIZATION_TYPES } from '../store'
import { ORGANIZATION_CUSTOMER_CODE, ORGANIZATION_SUPPLIER_CODE, ORGANIZATION_CUSTOMER_LEAD_CODE } from '@/const'
import { hasPerm } from '@/components/PermissionHelper'

import ThuxListMixin from '../../../../components/thux-list/ThuxListMixin'

import OrganizationInspectionsTable from './OrganizationInspectionsTable'
import OrganizationTechnicianEdit from './OrganizationInspectionEdit'
import { ROUTES as CUSTOMER_ORG_ROUTES } from '../organization/customer-organization/router'
import { ROUTES as OWNER_ORG_ROUTES } from '../organization/owner-organization/router'
import { ROUTES as LEAD_ORG_ROUTES } from '../organization/lead-organization/router'
import { ROUTES as SUPPLIER_ORG_ROUTES } from '../organization/supplier-organization/router'
import { TYPES as ORGANIZATION_INSPECTION_TYPES } from './store'
import OrganizationInspectionAttachmentsList from '../organization-inspection-attachment/OrganizationInspectionAttachmentsList'

export default {
  name: 'OrganizationInspectionsList',
  mixins: [ThuxListMixin],
  components: {
    OrganizationInspectionAttachmentsList,
    'component-table': OrganizationInspectionsTable,
    'component-edit': OrganizationTechnicianEdit
  },
  data () {
    return {
      ORGANIZATION_ROUTES,
      title: this.$t('Inspections'),
      rolePerm: ['organization_organizationinspection_list'],
      actionEnablePermission: ['organization_organizationinspection_enable'],
      actionDisablePermission: ['organization_organizationinspection_disable'],
      inspection: {},
      orgType: this.$route.params.orgType,
      showCommonFilters: false,
      breadcrumbs: [],
      showAttachments: false,
      searchFields: [
        { key: 'filter__number', placeholder: this.$t('Number'), type: 'number', col: 6 },
        { key: 'filter__date_inspection', placeholder: this.$t('Date inspection'), type: 'text', col: 6 },
        { key: 'filter__performer__first_name__icontains', placeholder: this.$t('Performer first name'), type: 'text', col: 6 },
        { key: 'filter__performer__last_name__icontains', placeholder: this.$t('Performer last name'), type: 'text', col: 6 },
        { key: 'filter__reason__icontains', placeholder: this.$t('Reason'), type: 'text', col: 12 }
      ]
    }
  },
  created () {
    this.organizationRetrieve({ id: this.$route.params.id })
    this.setBreadcrumbs()
  },
  computed: {
    ...mapGetters({
      ...ORGANIZATION_INSPECTION_TYPES.GENERIC.organization.organizationinspection.LIST.GETTERS,
      organizationDetail: ORGANIZATION_TYPES.GENERIC.organization.organization.DETAIL.GETTERS.detail
    }),
    titleWithOrganizationName () {
      if (this.organizationDetail && this.organizationDetail.code) {
        return `${this.$t('Inspections')} ${this.organizationDetail.name ? this.organizationDetail.name : this.organizationDetail.customer_name}`
      }
      if (this.list && this.list.results && this.list.results.length > 0) {
        const organizationName = this.list.results[0].organization_name ? `(${this.list.results[0].organization_name})` : ''
        return `${this.$t('Inspections')} ${organizationName}`
      }
      return this.$t('Inspections')
    },
    showList () {
      return true
    },
    canSeeActionSelect () {
      return false
    },
    canAddInstance () {
      return hasPerm('organization_organizationinspection_create')
    }
  },
  methods: {
    ...mapMutations({
      setSelectedList: ORGANIZATION_INSPECTION_TYPES.GENERIC.organization.organizationinspection.LIST.MUTATIONS.setSelectedList,
      setSelectAll: ORGANIZATION_INSPECTION_TYPES.GENERIC.organization.organizationinspection.LIST.MUTATIONS.setSelectAll
    }),
    ...mapActions({
      ...ORGANIZATION_INSPECTION_TYPES.GENERIC.organization.organizationinspection.LIST.ACTIONS,
      organizationRetrieve: ORGANIZATION_TYPES.GENERIC.organization.organization.DETAIL.ACTIONS.retrieve
    }),
    openEditForm (id) {
      this.$set(this, 'componentId', id)
      this.$set(this, 'inspection', { id: id })
      if (id) {
        this.$set(this, 'showEditForm', true)
        this.$set(this, 'showAttachments', true)
        this.$set(this, 'showDetailForm', false)
      } else {
        this.$set(this, 'showEditForm', true)
        this.$set(this, 'showAttachments', false)
        this.$set(this, 'showDetailForm', false)
      }
    },
    setBreadcrumbs () {
      if (this.orgType === ORGANIZATION_SUPPLIER_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: SUPPLIER_ORG_ROUTES.SUPPLIER_ORGANIZATION_LIST,
              // : CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_SUPPLIER_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: SUPPLIER_ORG_ROUTES.SUPPLIER_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_SUPPLIER_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else if (this.orgType === ORGANIZATION_CUSTOMER_LEAD_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: LEAD_ORG_ROUTES.LEAD_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_CUSTOMER_LEAD_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: LEAD_ORG_ROUTES.LEAD_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_CUSTOMER_LEAD_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else if (this.orgType === ORGANIZATION_CUSTOMER_CODE) {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organizations',
            route: {
              name: CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_LIST,
              params: { orgType: ORGANIZATION_CUSTOMER_CODE }
            }
          },
          {
            label: 'Organization',
            route: {
              name: CUSTOMER_ORG_ROUTES.CUSTOMER_ORGANIZATION_DETAIL,
              params: {
                orgType: ORGANIZATION_CUSTOMER_CODE,
                id: this.$route.params.id
              }
            }
          }
        ])
      } else {
        this.$set(this, 'breadcrumbs', [
          {
            label: 'Organization',
            route: {
              name: OWNER_ORG_ROUTES.OWNER_ORGANIZATION_DETAIL,
              params: {
                id: this.$route.params.id
              }
            }
          }
        ])
      }
    },
    getComponentList () {
      this.cleanAllData()
      this.setFilters({ filter__organization_id: this.$route.params.id })
    },
    showOrganizationInspectionAttachments (inspection) {
      this.$set(this, 'inspection', inspection)
      this.$set(this, 'showAttachments', true)
      if (this.componentId !== inspection.id) {
        this.$set(this, 'showEditForm', false)
        this.$set(this, 'showDetailForm', false)
      }
    },
    refreshListAfterSubmit () {
      this.getComponentList()
      this.closeEditForm()
    },
    closeEditForm () {
      this.$set(this, 'showDetailForm', false)
      this.$set(this, 'showEditForm', false)
      this.$set(this, 'showAttachments', false)
    }
  }
}
</script>
